import { useContext, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { EDIT_RECIPE_NAME } from '../../app/graphql/mutations';
import { RecipeState } from '../../app/recipePage/recipePage.types';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import { DialogCloseIcon } from '../../iconRef/recipeCreationPopupIcons';
import { ErrorManagementContext } from "../../utils/errorManagement/ErrorManagementProvider";
import { EventLogsEnum } from "../../utils/events";
import LoaderButton from '../buttons/loaderButton';
import styles from './editRecipeName.module.css'; // Import the new CSS module
import { RECIPE_NAME_REGEX } from './utils/constants';

const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => event.target.select();

const validateRecipeName = (name: string) => RECIPE_NAME_REGEX.test(name);

const EditRecipeName = ({
  editNameDialogOpen,
  setEditNameDialogOpen,
  oldRecipeData,
  recipeName,
  setRecipeName,
}: {
  editNameDialogOpen: boolean;
  setEditNameDialogOpen: (value: boolean) => void;
  oldRecipeData: RecipeState;
  recipeName: string;
  setRecipeName: (value: string) => void;
}) => {
  const [newRecipeName, setNewRecipeName] = useState<string>(recipeName);
  const [updateRecipeName] = useMutation(EDIT_RECIPE_NAME);
  const { logRecipeEdit } = useLogRecipeEdit();
  const { cookClubLog } = useContext(ErrorManagementContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [updatingName, setUpdatingName] = useState<boolean>(false);

  const handleClose = () => setEditNameDialogOpen(false);

  const handleDoneClick = async () => {
    try {
      setUpdatingName(true);

      await updateRecipeName({
        variables: {
          id: oldRecipeData.id,
          name: newRecipeName,
          lastEditedAt: new Date().toUTCString(),
        },
      });

      logRecipeEdit(EventLogsEnum.APP_RECIPE_NAME_EDIT_SAVED);
      setUpdatingName(validateRecipeName(newRecipeName));
      setRecipeName(newRecipeName);
      handleClose();
    } catch (error) {
      cookClubLog(
        "",
        "Something went wrong!",
        '',
        { ui: { type: "error" }, browserConsole: true }
      );
      handleClose();
      console.error("Error updating recipe name:", error);
    }
  };

  return (
    <Dialog
      id="edit-recipe-name-dialog"
      onClose={handleClose}
      open={editNameDialogOpen}
      PaperProps={{ className: styles.dialog_paper }}
    >
      <Grid id="dialog-items">
        <Grid className={styles.close_button} id="close-icon">
          <IconButton onClick={handleClose}>
            <DialogCloseIcon />
          </IconButton>
        </Grid>
        <Grid className={styles.container} id="recipe-name-input">
          <Typography variant="caption">Edit Recipe Name</Typography>
          <TextField
            autoFocus
            className={styles.text_field}
            FormHelperTextProps={{ className: styles.error_text }}
            fullWidth
            helperText={!validateRecipeName(newRecipeName) ? 'Please enter valid recipe name' : ''}
            id="recipe-name-field"
            inputProps={{ onFocus: handleFocus }}
            inputRef={inputRef}
            onChange={(e) => setNewRecipeName(e.target.value)}
            size="small"
            type="text"
            value={newRecipeName}
          />
          <Grid className={styles.done_button} id="done-button">
            <LoaderButton
              disabled={!validateRecipeName(newRecipeName)}
              loading={updatingName}
              onClick={handleDoneClick}
              text="Done"
            />
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default EditRecipeName;
